import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";
import { scrollToId as scrollTo } from "@/lib/scroll";

const PLANS = [
  {
    key: "basic",
    name: "Basic",
    price: "49 €",
    subtitle: "Der professionelle Einstieg",
    desc: "Für Selbstständige und kleinere Unternehmen, die einen einfachen, modernen und professionellen Webauftritt benötigen.",
    change: "Gelegentliche Änderungen inklusive",
    scope: "1 Onepager / Landingpage",
    total: "588 € brutto gesamt in 12 Monaten",
    features: [
      "1 professionelle Landingpage / Onepager",
      "Individuelles Design passend zum Unternehmen",
      "Domain inklusive",
      "Hosting inklusive",
      "SSL / HTTPS",
      "Optimierung für Smartphone, Tablet & Desktop",
      "Kontaktformular",
      "Klickbare Telefonnummer & E-Mail",
      "Google Maps Integration",
      "Öffnungszeiten & Leistungen",
      "Unternehmensinfos & Bilder",
      "Grundlegende SEO-Optimierung",
      "Technische Wartung",
      "Persönlicher Ansprechpartner",
      "Gelegentliche Änderungen inklusive",
    ],
    positioning: "Ideal, wenn Sie einfach professionell online präsent sein möchten.",
    highlight: false,
  },
  {
    key: "business",
    name: "Business",
    price: "79 €",
    subtitle: "Mehr Reichweite & Wirkung",
    desc: "Für Unternehmen, die ihre Website aktiv zur Kundengewinnung einsetzen möchten.",
    change: "Regelmäßige Änderungen inklusive",
    scope: "Bis zu 3 individuelle Seiten",
    total: "948 € brutto gesamt in 12 Monaten",
    features: [
      "Alles aus Basic",
      "Bis zu 3 individuelle Seiten",
      "Umfangreichere individuelle Gestaltung",
      "Mehrere Leistungsbereiche",
      "WhatsApp-Integration",
      "Kundenbewertungen & Referenzen",
      "FAQ-Bereich",
      "Bildergalerien",
      "Zusätzliche Call-to-Actions",
      "Stärkere Conversion-Optimierung",
      "Erweiterte SEO-Grundoptimierung",
      "Regelmäßige Änderungen inklusive",
      "Priorisierte Betreuung",
    ],
    positioning: "Ideal für Unternehmen, die online mehr erreichen möchten.",
    highlight: true,
  },
  {
    key: "premium",
    name: "Premium",
    price: "99 €",
    subtitle: "Maximale Flexibilität & Betreuung",
    desc: "Für Unternehmen mit umfangreicherem Webauftritt, die sich möglichst wenig selbst kümmern möchten.",
    change: "Erweiterter Änderungsservice",
    scope: "Bis zu 5 individuelle Seiten",
    total: "1.188 € brutto gesamt in 12 Monaten",
    features: [
      "Alles aus Business",
      "Bis zu 5 individuelle Seiten",
      "Höchste Individualisierung im Angebot",
      "Umfangreichere Leistungsdarstellung",
      "Zusätzliche individuelle Bereiche",
      "Erweiterte Conversion-Optimierung",
      "Erweiterte SEO-Grundoptimierung",
      "Erweiterter Änderungsservice",
      "Bevorzugte Bearbeitung von Änderungen",
      "Premium-Support · höchste Priorität",
      "Unterstützung bei der Weiterentwicklung",
    ],
    positioning: "Ideal für maximale Flexibilität bei möglichst wenig eigenem Aufwand.",
    highlight: false,
  },
];

export function selectPaket(name) {
  window.dispatchEvent(new CustomEvent("ls:selectPaket", { detail: name }));
  scrollTo("#kontakt");
}

function Card({ p, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col border ${
        p.highlight
          ? "border-[#FF3B00] bg-[#FF3B00]/[0.05] lg:-mt-5 lg:mb-0 shadow-2xl shadow-[#FF3B00]/10"
          : "border-white/10 bg-[#100b07]"
      }`}
      data-testid={`plan-${p.key}`}
    >
      {p.highlight && (
        <div className="absolute -top-px left-0 right-0 flex justify-center">
          <span className="font-tech text-[11px] uppercase tracking-[0.16em] bg-[#FF3B00] text-white px-4 py-1.5" data-testid="plan-badge-popular">
            Beliebteste Wahl
          </span>
        </div>
      )}
      <div className={`p-8 lg:p-9 ${p.highlight ? "pt-11" : ""}`}>
        <p className="font-display text-2xl text-white">{p.name}</p>
        <p className="font-tech text-xs uppercase tracking-[0.14em] text-[#FF3B00] mt-2">{p.subtitle}</p>
        <div className="flex items-end gap-2 mt-6">
          <span className="font-display text-6xl leading-none text-white" data-testid={`plan-price-${p.key}`}>{p.price}</span>
          <span className="text-neutral-400 text-sm mb-1.5">/ Monat brutto</span>
        </div>
        <p className="text-neutral-400 text-sm leading-relaxed mt-5 min-h-[64px]">{p.desc}</p>

        <button
          onClick={() => selectPaket(`${p.name} – ${p.price}`)}
          className={`group mt-7 w-full inline-flex items-center justify-center gap-2 font-tech text-sm uppercase tracking-[0.12em] py-4 transition-all duration-200 active:scale-[0.99] ${
            p.highlight
              ? "bg-[#FF3B00] text-white hover:bg-[#FF5C2B]"
              : "bg-white/5 text-white border border-white/15 hover:border-[#FF3B00] hover:text-[#FF3B00]"
          }`}
          data-testid={`plan-cta-${p.key}`}
        >
          {p.name} anfragen
          <ArrowUpRight size={17} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </button>
      </div>

      <div className="px-8 lg:px-9 pb-8 lg:pb-9 flex flex-col flex-1">
        <div className="border-t border-white/10 pt-6">
          <ul className="space-y-3">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-neutral-200 text-sm">
                <Check size={16} className="text-[#FF3B00] shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-auto pt-7 space-y-2 text-xs text-neutral-500">
          <p><span className="text-neutral-300">Umfang:</span> {p.scope} <span className="text-neutral-600">(Impressum & Datenschutz zählen nicht mit)</span></p>
          <p><span className="text-neutral-300">{p.total}</span></p>
          <p>12 Monate Mindestlaufzeit · danach monatlich flexibel</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Pricing() {
  return (
    <section id="preise" className="relative py-14 lg:py-20 border-t border-white/10 bg-[#0b0806]/70" data-testid="pricing">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <Reveal className="font-tech text-xs uppercase tracking-[0.25em] text-[#FF3B00] mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#FF3B00]" /> Unsere Pakete
          </Reveal>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight text-white uppercase mb-6">
            <MaskLineInView>Ein monatlicher Preis.</MaskLineInView>
            <MaskLineInView delay={0.1}>Alles inklusive.</MaskLineInView>
          </h2>
          <Reveal delay={0.15} className="text-neutral-400 text-base">
            Website, Domain, Hosting und Betreuung – zu einem festen monatlichen Preis. Wählen Sie
            das Paket, das zu Ihrem Unternehmen passt. Alle Preise brutto.
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:items-start">
          {PLANS.map((p, i) => (
            <Card key={p.key} p={p} i={i} />
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-10 text-center text-neutral-500 text-sm">
            <span className="text-white">12 Monate Mindestlaufzeit</span> – danach verlängert sich Ihr
            Website-Service jeweils nur um einen weiteren Monat. Kündigungsfrist: 1 Monat zum Ende der
            jeweiligen Laufzeit. Abrechnung monatlich.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
